import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/index.css";
import { hardenSonner } from "@/lib/toastHarden";
hardenSonner();
import { enableGlobalHaptics } from "@/hooks/useHapticTap";
enableGlobalHaptics();
import { bootPwaInstallCapture } from "@/lib/pwaInstall";
bootPwaInstallCapture();
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FeaturesProvider } from "@/lib/features";
import IdleLogout from "@/components/IdleLogout";
import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import Dashboard from "@/pages/Dashboard";
import InterviewsDashboard from "@/pages/InterviewsDashboard";
import JobsList from "@/pages/JobsList";
import JobDetail from "@/pages/JobDetail";
import CandidatesList from "@/pages/CandidatesList";
import PublicApply from "@/pages/PublicApply";
import QuickApply from "@/pages/QuickApply";
import SharedResume from "@/pages/SharedResume";
import PublicBookingPage from "@/pages/PublicBookingPage";
import Analytics from "@/pages/Analytics";
import Billing from "@/pages/Billing";
import Users from "@/pages/Users";
import RolePermissionsMatrix from "@/pages/settings/RolePermissionsMatrix";
import IntakeTemplateSettings from "@/pages/settings/IntakeTemplateSettings";
import StaffProfileSettings from "@/pages/settings/StaffProfileSettings";
import SessionsSettings from "@/pages/settings/SessionsSettings";
import AiJobsHistory from "@/pages/superadmin/AiJobsHistory";
import Clients from "@/pages/Clients";
import CandidateLayout from "@/components/CandidateLayout";
import CandidateDashboard from "@/pages/candidate/CandidateDashboard";
import CandidateJobs from "@/pages/candidate/CandidateJobs";
import CandidateSavedJobs from "@/pages/candidate/CandidateSavedJobs";
import CandidateProfile from "@/pages/candidate/CandidateProfile";
import CandidateNotificationSettings from "@/pages/candidate/CandidateNotificationSettings";
import UnifiedMessages from "@/pages/UnifiedMessages";
import CandidateReferrals from "@/pages/candidate/CandidateReferrals";
import CandidateSubscriptions from "@/pages/candidate/CandidateSubscriptions";
import AdminRevenue from "@/pages/AdminRevenue";
import AdminEmailHealth from "@/pages/AdminEmailHealth";
import AdminRenewalEmails from "@/pages/AdminRenewalEmails";
import SuperAdminConsole from "@/pages/SuperAdminConsole";
import SuperAdminWorkspaceDetail from "@/pages/SuperAdminWorkspaceDetail";
import SuperAdminSubscriptions from "@/pages/SuperAdminSubscriptions";
import SuperAdminFeatures from "@/pages/SuperAdminFeatures";
import SuperadminGrowthDashboard from "@/pages/superadmin/SuperadminGrowthDashboard";
import DropoffQueue from "@/pages/superadmin/DropoffQueue";
import ApiExplorer from "@/pages/superadmin/ApiExplorer";
import SuperAdminSchedulers from "@/pages/superadmin/Schedulers";
import ProgrammaticJobsLanding from "@/pages/ProgrammaticJobsLanding";
import GlobalPixelTracker from "@/components/GlobalPixelTracker";
import SuperAdminVerificationAnalytics from "@/pages/superadmin/VerificationAnalytics";
import SuperAdminDataHeal from "@/pages/superadmin/DataHeal";
import SuperAdminUsers from "@/pages/superadmin/Users";
import PartnerWaitlist from "@/pages/superadmin/PartnerWaitlist";
import PushHealth from "@/pages/superadmin/PushHealth";
import EmailCampaignPreview from "@/pages/superadmin/EmailCampaignPreview";
import CandidateSignals from "@/pages/superadmin/CandidateSignals";
import DuplicateCandidates from "@/pages/superadmin/DuplicateCandidates";
import CrmLeads from "@/pages/CrmLeads";
import Schedule from "@/pages/Schedule";
import SuperAdminAudit from "@/pages/SuperAdminAudit";
import AdminAuditLog from "@/pages/AdminAuditLog";
import PublicReferralLeaderboard from "@/pages/PublicReferralLeaderboard";
import PartnerLanding from "@/pages/PartnerLanding";
import PartnerActivate from "@/pages/PartnerActivate";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerLeadPool from "@/pages/partner/PartnerLeadPool";
import PartnerMarketplace from "@/pages/partner/PartnerMarketplace";
import PartnerSubmissions from "@/pages/partner/PartnerSubmissions";
import PartnerLogin from "@/pages/partner/PartnerLogin";
import CandidateSignup from "@/pages/candidate/CandidateSignup";
import CandidateWelcomePhoto from "@/pages/candidate/CandidateWelcomePhoto";
import CandidateMagicLanding from "@/pages/candidate/CandidateMagicLanding";
import PublicIntakeForm from "@/pages/candidate/PublicIntakeForm";
import PublicInterviewAssessment from "@/pages/candidate/PublicInterviewAssessment";
import CandidateLogin from "@/pages/candidate/CandidateLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import TermsOfService from "@/pages/legal/TermsOfService";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import CandidateTestimonials from "@/pages/candidate/CandidateTestimonials";
import MyData from "@/pages/candidate/MyData";
import CandidateBoost from "@/pages/candidate/CandidateBoost";
import CandidateVerifiedBadge from "@/pages/candidate/CandidateVerifiedBadge";
import CandidateInterviewPrep from "@/pages/candidate/CandidateInterviewPrep";
import CandidateFeatured from "@/pages/candidate/CandidateFeatured";
import CandidatePrivacy from "@/pages/candidate/CandidatePrivacy";
import CandidateBundle from "@/pages/candidate/CandidateBundle";
import PolicyPage from "@/pages/policies/PolicyPage";
import AboutUs from "@/pages/AboutUs";
import ContactUs from "@/pages/ContactUs";
import InterviewPrepPublic from "@/pages/InterviewPrepPublic";
import AdminVerifiedBadge from "@/pages/AdminVerifiedBadge";
import AdminParserCleanup from "@/pages/AdminParserCleanup";
import PublicReferencePage from "@/pages/PublicReferencePage";
import Marketplace from "@/pages/Marketplace";
import LandingPage from "@/pages/LandingPage";
// V1.6.2 — DaddyTalk components imported via the messenger extraction
// boundary. See `/app/frontend/src/messenger/README.md`.
import {
    CandidateMessages,
    LandingPage as DaddyTalkLandingPage,
    ChatPreview as DaddyTalkChatPreview,
    Inbox as DaddyTalkInbox,
    Settings as DaddyTalkSettings,
    AnnouncementBar as DaddyTalkAnnouncementBar,
    Launcher as DaddyTalkLauncher,
    TeamTalkInbox,
} from "@/messenger";
import PublicJobsBoard from "@/pages/PublicJobsBoard";
import NotificationsPage from "@/pages/NotificationsPage";
import PublicJobDetail from "@/pages/PublicJobDetail";
import PublicCareerPage from "@/pages/PublicCareerPage";
import RateMyCVPage from "@/pages/RateMyCVPage";
import ResumeStudioPage from "@/pages/ResumeStudioPage";
import ResumeReferralsPage from "@/pages/ResumeReferralsPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import SalaryBenchmarkPage from "@/pages/SalaryBenchmarkPage";
import ProPage from "@/pages/ProPage";
import InterviewQuestionsPage from "@/pages/InterviewQuestionsPage";
import ReferralPayouts from "@/pages/ReferralPayouts";
import TestimonialsModeration from "@/pages/TestimonialsModeration";
import WorkspaceSettings from "@/pages/WorkspaceSettings";
import PartnerAccessSettings from "@/pages/PartnerAccessSettings";
import PerformanceReport from "@/pages/PerformanceReport";
import Reports from "@/pages/Reports";
import NudgeQueue from "@/pages/NudgeQueue";
import { Toaster } from "@/components/ui/sonner";
import InstallPrompt from "@/components/InstallPrompt";

function RecruiterOnly({ children }) {
    const { user } = useAuth();
    if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#525252]">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "candidate") return <Navigate to="/candidate" replace />;
    return children;
}

// Route wrapper: only allow specified roles, else send them to /dashboard.
function RoleGate({ allow, children }) {
    const { user } = useAuth();
    if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#525252]">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (!allow.includes(user.role)) {
        // Superadmin isn't a regular admin — always land on the superadmin console.
        if (user.role === "superadmin") return <Navigate to="/superadmin" replace />;
        if (user.role === "partner") return <Navigate to="/partner/dashboard" replace />;
        if (user.role === "candidate") return <Navigate to="/candidate" replace />;
        return <Navigate to="/dashboard" replace />;
    }
    return children;
}

function CandidateOnly({ children }) {
    const { user } = useAuth();
    if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#525252]">Loading…</div>;
    if (!user) return <Navigate to="/candidate/login" replace />;
    if (user.role !== "candidate") return <Navigate to="/dashboard" replace />;
    return children;
}

function PublicOnly({ children }) {
    const { user } = useAuth();
    const loc = useLocation();
    if (user === null) return null;
    // Stay on the signup page after candidate signup until the inner page nav() resolves.
    if (user && loc.pathname === "/candidate/signup") return children;
    if (user) {
        if (user.role === "candidate") return <Navigate to="/candidate" replace />;
        if (user.role === "superadmin") return <Navigate to="/superadmin" replace />;
        if (user.role === "partner") return <Navigate to="/partner/dashboard" replace />;
        return <Navigate to="/dashboard" replace />;
    }
    return children;
}

export default function App() {
    return (
        <AuthProvider>
            <FeaturesProvider>
                <BrowserRouter>
                <IdleLogout />
                <GlobalPixelTracker />
                <DaddyTalkAnnouncementBar />
                <DaddyTalkLauncher />
                <Routes>
                    {/* Public candidate-facing landing page */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/daddytalk" element={<DaddyTalkLandingPage />} />
                    <Route path="/daddytalk/chat/:recruiterId" element={<DaddyTalkChatPreview />} />
                    <Route path="/candidate/messages" element={<RoleGate allow={["candidate", "admin", "recruiter", "hiring_manager", "superadmin"]}><CandidateMessages /></RoleGate>} />
                    <Route path="/jobs" element={<PublicJobsBoard />} />
                    <Route path="/jobs/:id" element={<PublicJobDetail />} />
                    <Route path="/jobs-in/:city/:role" element={<ProgrammaticJobsLanding />} />
                    <Route path="/careers/:slug" element={<PublicCareerPage />} />
                    {/* V2.0.48 (Feb 26, 2026) — client-branded canonical
                        for a job. Backend seo_job_meta returns
                        `canonical_url = /careers/{slug}/{jobId}` and puts
                        that in `og:url`; Facebook / LinkedIn treat og:url
                        as a canonical redirect and re-scrape that path.
                        Without this route the SPA fell through to `*` →
                        `Navigate to="/"` → LandingPage's OG tags won the
                        share preview. Reusing PublicJobDetail here works
                        because it only reads `useParams().id`. */}
                    <Route path="/careers/:slug/:id" element={<PublicJobDetail />} />
                    <Route path="/rate-my-cv" element={<RateMyCVPage />} />
                    <Route path="/resume" element={<ResumeStudioPage />} />
                    <Route path="/resume-studio" element={<ResumeStudioPage />} />
                    <Route path="/resume/referrals" element={<ResumeReferralsPage />} />
                    <Route path="/how-it-works" element={<HowItWorksPage />} />
                    <Route path="/salary-benchmark" element={<SalaryBenchmarkPage />} />
                    <Route path="/pro" element={<ProPage />} />
                    <Route path="/interview-questions" element={<InterviewQuestionsPage />} />
                    <Route path="/leaderboard" element={<PublicReferralLeaderboard />} />
                    <Route path="/partner" element={<PartnerLanding />} />
                    <Route path="/partner/login" element={<PartnerLogin />} />
                    <Route path="/partner/activate/:rowId" element={<PartnerActivate />} />
                    <Route path="/partner/dashboard" element={<RoleGate allow={["partner"]}><PartnerDashboard /></RoleGate>} />
                    <Route path="/partner/lead-pool" element={<RoleGate allow={["partner"]}><PartnerLeadPool /></RoleGate>} />
                    <Route path="/partner/marketplace" element={<RoleGate allow={["partner"]}><PartnerMarketplace /></RoleGate>} />
                    <Route path="/partner/submissions" element={<RoleGate allow={["partner"]}><PartnerSubmissions /></RoleGate>} />
                    <Route path="/freelance-recruiter" element={<PartnerLanding />} />
                    <Route path="/earn-with-us" element={<PartnerLanding />} />

                    <Route path="/apply/:id" element={<PublicApply />} />
                    <Route path="/quick-apply" element={<QuickApply />} />
                    <Route path="/quick-apply/:campaign" element={<QuickApply />} />
                    <Route path="/share/resume/:token" element={<SharedResume />} />
                    <Route path="/book/:token" element={<PublicBookingPage />} />
                    <Route path="/reference/:token" element={<PublicReferencePage />} />
                    {/* Login pages skip PublicOnly on purpose — each page
                        handles the "already signed in as wrong role" case
                        by clearing the mismatched session inline (see
                        Login.jsx + CandidateLogin.jsx useEffect). This
                        prevents the bug where clicking "Sign in" as a
                        candidate while a recruiter session is still active
                        bounces the user to /dashboard. */}
                    <Route path="/login" element={<Login />} />
                    <Route
                        path="/candidate/signup"
                        element={
                            <PublicOnly>
                                <CandidateSignup />
                            </PublicOnly>
                        }
                    />
                    <Route path="/candidate/login" element={<CandidateLogin />} />
                    <Route path="/candidate/magic/:token" element={<CandidateMagicLanding />} />
                    <Route path="/candidate/intake/:token" element={<PublicIntakeForm />} />
                    <Route path="/interview-assessment/:token" element={<PublicInterviewAssessment />} />
                    <Route
                        path="/forgot-password"
                        element={
                            <PublicOnly>
                                <ForgotPassword />
                            </PublicOnly>
                        }
                    />
                    <Route
                        path="/reset-password"
                        element={<ResetPassword />}
                    />
                    <Route
                        path="/accept-invite"
                        element={<AcceptInvite />}
                    />
                    <Route path="/legal/terms" element={<TermsOfService />} />
                    <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/contact" element={<ContactUs />} />
                    <Route path="/interview-prep" element={<InterviewPrepPublic />} />
                    <Route path="/policies/:slug" element={<PolicyPage />} />

                    {/* Candidate area */}
                    <Route
                        element={
                            <CandidateOnly>
                                <CandidateLayout />
                            </CandidateOnly>
                        }
                    >
                        <Route path="/candidate" element={<CandidateDashboard />} />
                        <Route path="/candidate/welcome-photo" element={<CandidateWelcomePhoto />} />
                        <Route path="/candidate/jobs" element={<CandidateJobs />} />
                        <Route path="/candidate/saved-jobs" element={<CandidateSavedJobs />} />
                        <Route path="/candidate/profile" element={<CandidateProfile />} />
                        <Route path="/candidate/settings/notifications" element={<CandidateNotificationSettings />} />
                        <Route path="/candidate/referrals" element={<CandidateReferrals />} />
                        <Route path="/candidate/subscriptions" element={<CandidateSubscriptions />} />
                        <Route path="/candidate/testimonials" element={<CandidateTestimonials />} />
                        <Route path="/candidate/boost" element={<CandidateBoost />} />
                        <Route path="/candidate/verified" element={<CandidateVerifiedBadge />} />
                        <Route path="/candidate/interview-prep" element={<CandidateInterviewPrep />} />
                        <Route path="/candidate/featured" element={<CandidateFeatured />} />
                        <Route path="/candidate/bundle" element={<CandidateBundle />} />
                        <Route path="/candidate/privacy" element={<CandidatePrivacy />} />
                        <Route path="/candidate/my-data" element={<MyData />} />
                    </Route>

                    {/* Recruiter area */}
                    <Route
                        element={
                            <RecruiterOnly>
                                <AppLayout />
                            </RecruiterOnly>
                        }
                    >
                        <Route path="/dashboard" element={<ErrorBoundary><RoleGate allow={["admin", "recruiter", "hiring_manager"]}><Dashboard /></RoleGate></ErrorBoundary>} />
                        <Route path="/today" element={<Navigate to="/crm?section=today" replace />} />
                        <Route path="/requirements" element={<ErrorBoundary><JobsList /></ErrorBoundary>} />
                        <Route path="/requirements/:id" element={<ErrorBoundary><JobDetail /></ErrorBoundary>} />
                        <Route path="/candidates" element={<ErrorBoundary><CandidatesList /></ErrorBoundary>} />
                        <Route path="/schedule" element={<Navigate to="/interviews" replace />} />
                        <Route path="/interviews" element={<InterviewsDashboard />} />
                        <Route path="/clients" element={<Clients />} />
                        <Route path="/referrals" element={<RoleGate allow={["admin"]}><ReferralPayouts /></RoleGate>} />
                        <Route path="/notifications" element={<NotificationsPage />} />
                        <Route path="/testimonials" element={<RoleGate allow={["admin", "hiring_manager"]}><TestimonialsModeration /></RoleGate>} />
                        <Route path="/billing" element={<RoleGate allow={["admin"]}><Billing /></RoleGate>} />
                        <Route path="/revenue" element={<RoleGate allow={["superadmin"]}><AdminRevenue /></RoleGate>} />
                        <Route path="/admin/email-health" element={<RoleGate allow={["superadmin"]}><AdminEmailHealth /></RoleGate>} />
                        <Route path="/admin/renewal-emails" element={<RoleGate allow={["superadmin"]}><AdminRenewalEmails /></RoleGate>} />
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/performance" element={<RoleGate allow={["admin", "recruiter", "hiring_manager"]}><PerformanceReport /></RoleGate>} />
                        <Route path="/reports" element={<RoleGate allow={["admin", "hiring_manager"]}><Reports /></RoleGate>} />
                        <Route path="/nudge-queue" element={<RoleGate allow={["admin", "recruiter", "hiring_manager"]}><NudgeQueue /></RoleGate>} />
                        <Route path="/messages" element={<RoleGate allow={["admin", "recruiter", "hiring_manager", "superadmin"]}><UnifiedMessages /></RoleGate>} />
                        <Route path="/team-messages" element={<RoleGate allow={["admin", "recruiter", "hiring_manager", "superadmin"]}><Navigate to="/messages?tab=team" replace /></RoleGate>} />
                        <Route path="/settings/daddytalk" element={<RoleGate allow={["admin", "hiring_manager", "superadmin"]}><DaddyTalkSettings /></RoleGate>} />
                        <Route path="/users" element={<Users />} />
                        <Route path="/settings/roles" element={<RoleGate allow={["admin", "hiring_manager", "recruiter", "superadmin"]}><RolePermissionsMatrix /></RoleGate>} />
                        <Route path="/settings/screening-intake" element={<RoleGate allow={["admin", "superadmin"]}><IntakeTemplateSettings /></RoleGate>} />
                        <Route path="/settings/profile" element={<RoleGate allow={["admin", "recruiter", "hiring_manager", "superadmin"]}><StaffProfileSettings /></RoleGate>} />
                        <Route path="/settings/sessions" element={<RoleGate allow={["admin", "recruiter", "hiring_manager", "superadmin"]}><SessionsSettings /></RoleGate>} />
                        <Route path="/candidate/settings/sessions" element={<RoleGate allow={["candidate"]}><SessionsSettings /></RoleGate>} />
                        <Route path="/superadmin/ai-jobs" element={<RoleGate allow={["admin", "superadmin"]}><AiJobsHistory /></RoleGate>} />
                        <Route path="/superadmin/push-health" element={<RoleGate allow={["admin", "superadmin"]}><PushHealth /></RoleGate>} />
                        <Route path="/superadmin/email-campaign" element={<RoleGate allow={["admin", "superadmin"]}><EmailCampaignPreview /></RoleGate>} />
                        <Route path="/superadmin/candidate-signals" element={<RoleGate allow={["superadmin"]}><CandidateSignals /></RoleGate>} />
                        <Route path="/superadmin/duplicates" element={<RoleGate allow={["superadmin", "admin"]}><DuplicateCandidates /></RoleGate>} />
                        <Route path="/crm" element={<RoleGate allow={["superadmin", "admin", "recruiter", "hiring_manager"]}><CrmLeads /></RoleGate>} />
                        <Route path="/settings" element={<RoleGate allow={["admin"]}><WorkspaceSettings /></RoleGate>} />
                        <Route path="/settings/partner-access" element={<RoleGate allow={["admin", "superadmin"]}><PartnerAccessSettings /></RoleGate>} />
                        <Route path="/superadmin" element={<RoleGate allow={["superadmin"]}><SuperAdminConsole /></RoleGate>} />
                        <Route path="/superadmin/workspaces/:id" element={<RoleGate allow={["superadmin"]}><SuperAdminWorkspaceDetail /></RoleGate>} />
                        <Route path="/superadmin/subscriptions" element={<RoleGate allow={["superadmin"]}><SuperAdminSubscriptions /></RoleGate>} />
                        <Route path="/superadmin/features" element={<RoleGate allow={["superadmin"]}><SuperAdminFeatures /></RoleGate>} />
                        <Route path="/superadmin/growth" element={<RoleGate allow={["superadmin"]}><SuperadminGrowthDashboard /></RoleGate>} />
                        <Route path="/superadmin/dropoff" element={<RoleGate allow={["superadmin", "admin"]}><DropoffQueue /></RoleGate>} />
                        <Route path="/superadmin/audit" element={<RoleGate allow={["superadmin"]}><SuperAdminAudit /></RoleGate>} />
                        <Route path="/superadmin/api-explorer" element={<RoleGate allow={["superadmin"]}><ApiExplorer /></RoleGate>} />
                        <Route path="/superadmin/schedulers" element={<RoleGate allow={["superadmin"]}><SuperAdminSchedulers /></RoleGate>} />
                        <Route path="/superadmin/verification" element={<RoleGate allow={["superadmin"]}><SuperAdminVerificationAnalytics /></RoleGate>} />
                        <Route path="/superadmin/data-heal" element={<RoleGate allow={["superadmin"]}><SuperAdminDataHeal /></RoleGate>} />
                        <Route path="/superadmin/users" element={<RoleGate allow={["superadmin"]}><SuperAdminUsers /></RoleGate>} />
                        <Route path="/superadmin/partner-waitlist" element={<RoleGate allow={["superadmin", "admin"]}><PartnerWaitlist /></RoleGate>} />
                        <Route path="/admin/audit-log" element={<RoleGate allow={["admin"]}><AdminAuditLog /></RoleGate>} />
                        <Route path="/admin/verified-badge" element={<RoleGate allow={["superadmin"]}><AdminVerifiedBadge /></RoleGate>} />
                        <Route path="/admin/parser-cleanup" element={<RoleGate allow={["admin", "hiring_manager", "superadmin"]}><AdminParserCleanup /></RoleGate>} />
                        <Route path="/marketplace" element={<RoleGate allow={["admin", "recruiter", "hiring_manager", "superadmin"]}><Marketplace /></RoleGate>} />
                    </Route>

                    {/* Catch-all: send unknown paths to the landing page */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <Toaster position="top-right" richColors />
                <InstallPrompt />
            </BrowserRouter>
            </FeaturesProvider>
        </AuthProvider>
    );
}
